<template>
    <filter-panel
        :filterOptions="filterOptions"
        :collapsed="true"
        :model-value="filterState"
        @update:modelValue="onUpdateFilters"
    />
    <v-data-table
        :headers="sampleSOPHeaders"
        :items="filteredSampleSOPs"
        item-key="id"
        class="elevation-1"
        :items-per-page="25"
        :search="search"
    >
        <template v-slot:item.sampleTypes="{ item }">
            {{ item.sampleType || "N/A" }}
        </template>
        <template v-slot:item.sampleDescriptors="{ item }">
            {{ item.sampleDescriptors.join(", ") || "N/A" }}
        </template>
        <template v-slot:item.descriptionUrl="{ item }">
            <a v-if="item.descriptionUrl" :href="item.descriptionUrl" target="_blank">View SOP</a>
            <span v-else>N/A</span>
        </template>
        <template v-slot:item.actions="{ item }">
            <v-row>
                <v-icon @click="editSampleSOP(item)">$edit</v-icon>
            </v-row>
        </template>
    </v-data-table>
</template>

<script setup lang="ts">
import { buildFilterOptionsFromData, filterData } from "@/lib/filterUtils";
import { MaterialType, SampleDescriptor, SavedMaterialSampleSOP } from "@/services/samples/samplesAPI";
import { ref, computed, watch } from "vue";
import FilterPanel from "../common/FilterPanel.vue";

const { sampleSOPs, search, materialTypes, sampleDescriptors } = defineProps<{
    sampleSOPs: SavedMaterialSampleSOP[];
    materialTypes: MaterialType[];
    sampleDescriptors: SampleDescriptor[];
    search: string;
}>();

const filterState = ref(<Record<string, string[]>>{});
const filterConfig = ref([
    { name: "name", prop: "name" },
]);

const filterOptions = ref({});
const filteredSampleSOPs = computed(() => {
    return filterData(sampleSopRows.value, filterState.value, filterConfig.value);
});

const sampleSopRows = computed(() => {
    return sampleSOPs.map(mapSopToRow);
});

function mapSopToRow(sop: SavedMaterialSampleSOP) {
    const sampleType = materialTypes.find((mt) => mt.id === sop.materialTypeId)?.name;
    const sampleDescriptorsList = sampleDescriptors
        .filter((sd) => sop.sampleDescriptorIds.includes(sd.id))
        .map((sd) => sd.name);
    return {
        ...sop,
        sampleType,
        sampleDescriptors: sampleDescriptorsList,
    };
}

watch(
    () => sampleSOPs,
    (newSampleSOPs) => {
        filterOptions.value = buildFilterOptionsFromData(newSampleSOPs.map(mapSopToRow), filterConfig.value);
    },
    { immediate: true }
);

const emit = defineEmits<{
    (e: "editSamplesSOP", item: SavedMaterialSampleSOP): void;
}>();

const sampleSOPHeaders = ref([
    { title: "SOP ID", value: "code", sortable: true },
    { title: "SOP Name", value: "name", sortable: true },
    { title: "Sample Types", value: "sampleTypes", sortable: false },
    { title: "Sample Descriptors", value: "sampleDescriptors", sortable: false },
    { title: "Description", value: "descriptionUrl", sortable: false },
    { title: "Actions", value: "actions", sortable: false },
]);

function editSampleSOP(item: SavedMaterialSampleSOP) {
    emit("editSamplesSOP", sampleSOPs.find((s) => s.id === item.id)!);
}

function onUpdateFilters(newFilterState: Record<string, string[]>) {
    filterState.value = newFilterState;
}
</script>
